import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Plus,
  Edit2,
  Trash2,
  Calendar,
  MapPin,
  Clock,
  X,
  Ticket,
  Users,
  ExternalLink,
  RefreshCw,
  CloudDownload,
  Globe,
} from 'lucide-react';
import { db } from '../../services/firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { fetchArtistEvents, getNewEvents } from '../../services/bandsintown';

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  if (/AM|PM/i.test(timeStr)) return timeStr; // already 12h
  try {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  } catch { return timeStr; }
};

export default function AdminShows() {
  const [shows, setShows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingShow, setEditingShow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    venue: '',
    address: '',
    city: '',
    state: '',
    date: '',
    time: '',
    ticketUrl: '',
    ticketPrice: '',
    status: 'upcoming',
    description: '',
    capacity: '',
    ageRestriction: 'all',
  });

  useEffect(() => {
    if (!db) return;

    const q = query(collection(db, 'shows'), orderBy('date', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const showsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setShows(showsData);
    });

    return unsubscribe;
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const showData = {
        ...formData,
        updatedAt: new Date(),
      };

      if (editingShow) {
        await updateDoc(doc(db, 'shows', editingShow.id), showData);
      } else {
        showData.createdAt = new Date();
        await addDoc(collection(db, 'shows'), showData);
      }

      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving show:', error);
      alert('Failed to save show');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this show?')) return;

    try {
      await deleteDoc(doc(db, 'shows', id));
    } catch (error) {
      console.error('Error deleting show:', error);
      alert('Failed to delete show');
    }
  };

  const handleEdit = (show) => {
    setEditingShow(show);
    setFormData({
      title: show.title || '',
      venue: show.venue || '',
      address: show.address || '',
      city: show.city || '',
      state: show.state || '',
      date: show.date || '',
      time: show.time || '',
      ticketUrl: show.ticketUrl || '',
      ticketPrice: show.ticketPrice || '',
      status: show.status || 'upcoming',
      description: show.description || '',
      capacity: show.capacity || '',
      ageRestriction: show.ageRestriction || 'all',
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingShow(null);
    setFormData({
      title: '',
      venue: '',
      address: '',
      city: '',
      state: '',
      date: '',
      time: '',
      ticketUrl: '',
      ticketPrice: '',
      status: 'upcoming',
      description: '',
      capacity: '',
      ageRestriction: 'all',
    });
  };

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  // Sync from Bandsintown
  const handleSyncBandsintown = async () => {
    setIsSyncing(true);
    setSyncStatus(null);

    try {
      // Fetch events from Bandsintown
      const bandsintownEvents = await fetchArtistEvents('Sirius Company');

      if (bandsintownEvents.length === 0) {
        setSyncStatus({ type: 'info', message: 'No events found on Bandsintown. Make sure the artist profile exists.' });
        return;
      }

      // Find new events that don't exist in Firestore
      const newEvents = getNewEvents(bandsintownEvents, shows);

      if (newEvents.length === 0) {
        setSyncStatus({ type: 'success', message: 'All events are already synced!' });
        return;
      }

      // Add new events to Firestore
      let addedCount = 0;
      for (const event of newEvents) {
        await addDoc(collection(db, 'shows'), event);
        addedCount++;
      }

      setSyncStatus({
        type: 'success',
        message: `Successfully imported ${addedCount} new show${addedCount > 1 ? 's' : ''} from Bandsintown!`,
      });
    } catch (error) {
      console.error('Error syncing from Bandsintown:', error);
      setSyncStatus({ type: 'error', message: 'Failed to sync from Bandsintown. Please try again.' });
    } finally {
      setIsSyncing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming':
        return 'bg-green-500';
      case 'soldout':
        return 'bg-red-500';
      case 'cancelled':
        return 'bg-gray-500';
      case 'completed':
        return 'bg-blue-500';
      default:
        return 'bg-orange-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Shows & Events</h2>
          <p className="text-gray-400">Manage upcoming performances and events</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleSyncBandsintown}
            disabled={isSyncing}
            variant="outline"
            className="border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white"
          >
            {isSyncing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CloudDownload className="w-4 h-4 mr-2" />
            )}
            {isSyncing ? 'Syncing...' : 'Sync Bandsintown'}
          </Button>
          <Button
            onClick={openAddModal}
            className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Show
          </Button>
        </div>
      </div>

      {/* Sync Status */}
      {syncStatus && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center gap-3 ${
            syncStatus.type === 'success'
              ? 'bg-green-500/20 border border-green-500/30 text-green-400'
              : syncStatus.type === 'error'
              ? 'bg-red-500/20 border border-red-500/30 text-red-400'
              : 'bg-blue-500/20 border border-blue-500/30 text-blue-400'
          }`}
        >
          <Globe className="w-5 h-5 flex-shrink-0" />
          <span>{syncStatus.message}</span>
          <button
            onClick={() => setSyncStatus(null)}
            className="ml-auto text-current hover:opacity-70"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      {/* Shows List */}
      <div className="space-y-4">
        {shows.map((show) => (
          <motion.div
            key={show.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="hover:border-orange-500/50 transition-all">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Date Box */}
                  <div className="flex items-start gap-4">
                    <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-lg p-3 text-center min-w-[70px]">
                      <div className="text-2xl font-bold text-white">
                        {show.date ? (() => { const [y,m,d] = show.date.split('-').map(Number); return new Date(y, m-1, d).getDate(); })() : '??'}
                      </div>
                      <div className="text-xs text-white/80 uppercase">
                        {show.date
                          ? (() => { const [y,m,d] = show.date.split('-').map(Number); return new Date(y, m-1, d).toLocaleDateString('en-US', { month: 'short' }); })()
                          : 'TBA'}
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-bold text-white text-lg">{show.title}</h3>
                        <span
                          className={`px-2 py-0.5 text-xs font-semibold rounded capitalize text-white ${getStatusColor(
                            show.status
                          )}`}
                        >
                          {show.status}
                        </span>
                        {show.source === 'bandsintown' && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded bg-blue-500/20 text-blue-400 flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            Bandsintown
                          </span>
                        )}
                      </div>
                      <p className="text-orange-400 font-medium">{show.venue}</p>
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {show.city}, {show.state}
                        </span>
                        {show.time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatTime(show.time)}
                          </span>
                        )}
                        {show.ticketPrice && (
                          <span className="flex items-center gap-1">
                            <Ticket className="w-4 h-4" />${show.ticketPrice}
                          </span>
                        )}
                        {show.capacity && (
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {show.capacity} cap
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 md:flex-shrink-0">
                    {show.ticketUrl && (
                      <a
                        href={show.ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button size="sm" variant="outline" className="border-green-500 text-green-500 hover:bg-green-500 hover:text-white">
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Tickets
                        </Button>
                      </a>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(show)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(show.id)}
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {shows.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No shows scheduled</h3>
            <p className="text-gray-400 mb-4">Add your first show to get started</p>
            <Button onClick={openAddModal} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add Show
            </Button>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-gray-900 p-6 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">
                {editingShow ? 'Edit Show' : 'Add New Show'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Event Title *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Go-Go Night Live"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="venue">Venue *</Label>
                  <Input
                    id="venue"
                    name="venue"
                    value={formData.venue}
                    onChange={handleChange}
                    placeholder="Venue name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Street address"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Washington"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="DC"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time">Time</Label>
                  <Input
                    id="time"
                    name="time"
                    type="time"
                    value={formData.time}
                    onChange={handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white"
                  >
                    <option value="upcoming">Upcoming</option>
                    <option value="soldout">Sold Out</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="ticketPrice">Ticket Price ($)</Label>
                  <Input
                    id="ticketPrice"
                    name="ticketPrice"
                    value={formData.ticketPrice}
                    onChange={handleChange}
                    placeholder="25"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="capacity">Capacity</Label>
                  <Input
                    id="capacity"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    placeholder="500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ageRestriction">Age Restriction</Label>
                  <select
                    id="ageRestriction"
                    name="ageRestriction"
                    value={formData.ageRestriction}
                    onChange={handleChange}
                    className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white"
                  >
                    <option value="all">All Ages</option>
                    <option value="18+">18+</option>
                    <option value="21+">21+</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ticketUrl">Ticket URL</Label>
                <Input
                  id="ticketUrl"
                  name="ticketUrl"
                  value={formData.ticketUrl}
                  onChange={handleChange}
                  placeholder="https://tickets.example.com/..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Event description..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white placeholder:text-gray-500"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                >
                  {isLoading ? 'Saving...' : editingShow ? 'Update Show' : 'Add Show'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
